// Conflict checking — PI Platform outline v2 §3.6.
//
// The daily task this exists for: before opening a matter, search every
// name involved against the firm's existing parties and record that you
// looked.
//
// Two things the screen has to communicate, because they are the two
// things the backend enforces and a UI that hides them would make the
// enforcement feel arbitrary:
//
//   Nothing auto-clears. Every hit needs a human disposition, and the
//   check cannot be closed until they all have one. So the close button
//   states what is outstanding rather than just refusing.
//
//   A clean result still gets recorded. The "no matches" state is
//   deliberately not a dead end — it says the check was saved, because
//   that record is the artefact the firm actually needs later.
//
// Deliberately not colour-coded by severity. An adverse hit is marked
// with a word, not a red background: nobody has defined what amber
// versus red would mean here, and inventing a severity scale for a
// bar-complaint-adjacent decision would be worse than plain text.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const MATCH_LABELS = {
  exact: "Exact match",
  nickname: "Name variant",
  partial: "Partial match",
  last_name: "Surname only",
};

const MATCH_HINTS = {
  exact: "identical after normalising",
  nickname: "e.g. Bob / Robert",
  partial: "one name contains the other",
  last_name: "shared surname — often unrelated",
};

const DISPOSITIONS = [
  { id: "not_a_conflict", label: "Not a conflict" },
  { id: "conflict", label: "Conflict" },
  { id: "waived", label: "Waived" },
];

export function ConflictCheckPage() {
  const [names, setNames] = useState([""]);
  const [matterRef, setMatterRef] = useState("");
  const [running, setRunning] = useState(false);
  const [check, setCheck] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [index, setIndex] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const d = await apiFetch("/api/conflicts/checks");
      setHistory(d.data || []);
    } catch (e) {
      // History is context, not the task. Failing to load it should not
      // stop somebody running a check.
      console.warn("conflict history:", e.message);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    apiFetch("/api/conflicts/index").then(setIndex).catch(() => setIndex(null));
  }, []);

  const setName = (i, v) =>
    setNames((prev) => prev.map((n, j) => (j === i ? v : n)));

  const run = async () => {
    const wanted = names.map((n) => n.trim()).filter(Boolean);
    if (!wanted.length) {
      setError("Enter at least one name to search.");
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const d = await apiFetch("/api/conflicts/check", {
        method: "POST",
        body: JSON.stringify({
          names: wanted,
          intake_reference: matterRef.trim() || null,
        }),
      });
      setCheck(d);
      await loadHistory();
    } catch (e) {
      setError(e.message);
    }
    setRunning(false);
  };

  const openCheck = async (id) => {
    try {
      setCheck(await apiFetch(`/api/conflicts/checks/${id}`));
    } catch (e) {
      setError(e.message);
    }
  };

  const disposition = async (hit, value) => {
    const note = (noteDraft[hit.id] || "").trim();
    if ((value === "conflict" || value === "waived") && !note) {
      setError(
        "Recording a conflict or a waiver needs a note saying why — that "
        + "sentence is the part that matters later.");
      return;
    }
    setError(null);
    try {
      await apiFetch(`/api/conflict-hits/${hit.id}`, {
        method: "PATCH",
        body: JSON.stringify({ disposition: value, note: note || null }),
      });
      setCheck(await apiFetch(`/api/conflicts/checks/${check.id}`));
    } catch (e) {
      setError(e.message);
    }
  };

  const resolve = async (status) => {
    const note = (noteDraft.__check || "").trim();
    try {
      const d = await apiFetch(`/api/conflicts/checks/${check.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ status, note: note || null }),
      });
      setCheck(d);
      await loadHistory();
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const hits = check?.hits || [];
  const pending = hits.filter((h) => h.disposition === "unreviewed").length;
  const closed = check && check.status !== "open";

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "900px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "32px",
               color: C.navy, marginBottom: "6px" },
    }, "Conflict check"),
    React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "24px" },
    }, "Search everyone involved against the firm's existing parties. "
     + "Every check is recorded, including the ones that find nothing."),

    error && React.createElement("div", {
      style: {
        fontSize: "13px", color: C.textDark, background: C.warmWhite,
        border: `1px solid ${C.warmGray}`, borderRadius: "8px",
        padding: "11px 14px", marginBottom: "16px",
      },
    }, error),

    // An empty index is stated before anyone runs anything. A search
    // over nothing returns no hits, which looks identical to a genuinely
    // clean check — and that is the one result nobody should ever be
    // shown without this caveat attached.
    index && index.is_empty && React.createElement("div", {
      style: {
        fontSize: "13px", color: C.textDark, background: C.warmWhite,
        border: `1px solid ${C.warmGray}`, borderRadius: "8px",
        padding: "13px 16px", marginBottom: "18px", lineHeight: 1.55,
      },
    },
      React.createElement("strong", {
        style: { display: "block", marginBottom: "4px", color: C.navy },
      }, "Nothing is indexed yet"),
      "No parties or contacts have been synced for this firm, so a check "
      + "would search nothing and come back clean. That is not a clean "
      + "result. Run the matter, contact and party syncs first — until "
      + "then, checks are recorded but stay open, marked as covering "
      + "nothing.",
    ),

    // ── The search ──────────────────────────────────────────────
    React.createElement("div", { style: cardStyle },
      React.createElement("h2", { style: h2Style }, "Names to search"),
      React.createElement("p", {
        style: { fontSize: "12px", color: C.textMuted, margin: "6px 0 14px" },
      }, "Client, opposing parties, insurers, anyone else involved. "
       + "Nicknames and surname-first spellings are handled — search the "
       + "name as you have it."),

      ...names.map((n, i) =>
        React.createElement("div", {
          key: i,
          style: { display: "flex", gap: "8px", marginBottom: "8px" },
        },
          React.createElement("input", {
            value: n,
            onChange: (e) => setName(i, e.target.value),
            onKeyDown: (e) => { if (e.key === "Enter") run(); },
            placeholder: i === 0 ? "e.g. Robert Grieve" : "Another name",
            style: inputStyle,
          }),
          names.length > 1 && React.createElement("button", {
            onClick: () => setNames((p) => p.filter((_, j) => j !== i)),
            style: quietBtn, title: "Remove",
          }, "Remove"),
        )),

      React.createElement("button", {
        onClick: () => setNames((p) => [...p, ""]), style: quietBtn,
      }, "Add another name"),

      React.createElement("div", { style: { marginTop: "16px" } },
        React.createElement("label", {
          style: { fontSize: "12px", color: C.textMuted, display: "block",
                   marginBottom: "5px" },
        }, "Reference (optional) — intake name or matter number, so this "
         + "check can be found again"),
        React.createElement("input", {
          value: matterRef,
          onChange: (e) => setMatterRef(e.target.value),
          placeholder: "e.g. Grieve intake, 12 Aug",
          style: inputStyle,
        }),
      ),

      React.createElement("div", { style: { marginTop: "16px" } },
        React.createElement("button", {
          onClick: run, disabled: running, style: primaryBtn,
        }, running ? "Searching…" : "Run conflict check"),
      ),
    ),

    running && React.createElement("div", {
      style: { display: "flex", gap: "8px", alignItems: "center",
               padding: "20px", color: C.textMuted, fontSize: "13px" },
    }, React.createElement(Spinner, { size: 16 }), "Searching the firm's parties…"),

    // ── Result ──────────────────────────────────────────────────
    check && React.createElement("div", { style: cardStyle },
      React.createElement("div", {
        style: { display: "flex", alignItems: "baseline", gap: "10px",
                 flexWrap: "wrap", marginBottom: "4px" },
      },
        React.createElement("h2", { style: h2Style }, "Result"),
        React.createElement("div", { style: { flex: 1 } }),
        React.createElement("span", {
          style: { fontSize: "11px", color: C.textMuted },
        }, `Check #${check.id} · ${check.run_at ? check.run_at.slice(0, 10) : ""}`
         + (check.run_by ? ` · ${check.run_by}` : "")),
      ),

      hits.length === 0
        ? React.createElement("p", {
            style: { fontSize: "13px", color: C.textDark, margin: "10px 0 0" },
          }, check.index_was_empty
              ? "No hits — but nothing is indexed for this firm, so this "
                + "search covered nothing. It has been recorded as such and "
                + "left open, because a clean result over an empty index is "
                + "not a clean result."
              : `No matching parties or contacts, searched against `
                + `${check.parties_searched} part`
                + `${check.parties_searched === 1 ? "y" : "ies"} and `
                + `${check.contacts_searched} contact`
                + `${check.contacts_searched === 1 ? "" : "s"}. `
                + "The check has been saved — a clean result is the record "
                + "that somebody looked.")
        : React.createElement("div", null,
            React.createElement("p", {
              style: { fontSize: "13px", color: C.textDark, margin: "8px 0 16px" },
            }, `${hits.length} possible match${hits.length === 1 ? "" : "es"}`
             + (check.adverse_hit_count
                  ? `, ${check.adverse_hit_count} on the other side of a matter.`
                  : ".")
             + " Names match loosely on purpose — most of these will be "
             + "different people, and saying so is part of the record."),

            ...hits.map((h) => React.createElement("div", {
              key: h.id,
              style: {
                borderTop: `1px solid ${C.warmGray}`, padding: "14px 0",
              },
            },
              React.createElement("div", {
                style: { display: "flex", gap: "8px", alignItems: "baseline",
                         flexWrap: "wrap" },
              },
                React.createElement("span", {
                  style: { fontSize: "14px", fontWeight: 600, color: C.navy },
                }, h.matched_name),
                h.is_adverse && React.createElement("span", { style: tagStyle },
                  "Adverse party"),
                React.createElement("span", { style: tagStyle },
                  MATCH_LABELS[h.match_type] || h.match_type),
                React.createElement("div", { style: { flex: 1 } }),
                h.disposition !== "unreviewed" && React.createElement("span", {
                  style: { fontSize: "11px", color: C.textMuted },
                }, `${DISPOSITIONS.find((d) => d.id === h.disposition)?.label
                     || h.disposition}`
                   + (h.reviewed_by ? ` · ${h.reviewed_by}` : "")),
              ),

              React.createElement("div", {
                style: { fontSize: "12px", color: C.textMuted, marginTop: "3px" },
              },
                `Searched "${h.searched_name}" · ${MATCH_HINTS[h.match_type] || ""}`
                + (h.matter_id ? ` · matter ${h.matter_display_number || h.matter_id}` : " · contact only, no matter")
                + (h.party_role ? ` · ${h.party_role.replace(/_/g, " ")}` : "")),

              h.note && React.createElement("div", {
                style: { fontSize: "12px", color: C.textDark, marginTop: "6px",
                         background: C.warmWhite, borderRadius: "6px",
                         padding: "8px 10px" },
              }, h.note),

              !closed && React.createElement("div", { style: { marginTop: "9px" } },
                React.createElement("input", {
                  value: noteDraft[h.id] || "",
                  onChange: (e) => setNoteDraft((p) => ({ ...p, [h.id]: e.target.value })),
                  placeholder: "Why is this a conflict, or why isn't it? (required for Conflict / Waived)",
                  style: { ...inputStyle, marginBottom: "8px" },
                }),
                React.createElement("div", {
                  style: { display: "flex", gap: "8px", flexWrap: "wrap" },
                },
                  ...DISPOSITIONS.map((d) => React.createElement("button", {
                    key: d.id,
                    onClick: () => disposition(h, d.id),
                    style: h.disposition === d.id ? primaryBtn : quietBtn,
                  }, d.label)),
                ),
              ),
            )),
          ),

      // ── Closing the check ───────────────────────────────────
      hits.length > 0 && !closed && React.createElement("div", {
        style: { borderTop: `1px solid ${C.warmGray}`, marginTop: "8px",
                 paddingTop: "16px" },
      },
        pending > 0
          ? React.createElement("p", {
              style: { fontSize: "13px", color: C.textDark, margin: 0 },
            }, `${pending} match${pending === 1 ? "" : "es"} still need a `
             + "decision. Closing the check means saying somebody looked at "
             + "each one, so it stays open until they have.")
          : React.createElement("div", null,
              React.createElement("input", {
                value: noteDraft.__check || "",
                onChange: (e) => setNoteDraft((p) => ({ ...p, __check: e.target.value })),
                placeholder: "Note on the decision (required to block or waive)",
                style: { ...inputStyle, marginBottom: "10px" },
              }),
              React.createElement("div", {
                style: { display: "flex", gap: "8px", flexWrap: "wrap" },
              },
                React.createElement("button", {
                  onClick: () => resolve("cleared"), style: primaryBtn,
                }, "Clear — no conflict"),
                React.createElement("button", {
                  onClick: () => resolve("blocked"), style: quietBtn,
                }, "Block — cannot take"),
                React.createElement("button", {
                  onClick: () => resolve("waived"), style: quietBtn,
                }, "Waived in writing"),
              ),
            ),
      ),

      closed && React.createElement("div", {
        style: { borderTop: `1px solid ${C.warmGray}`, marginTop: "8px",
                 paddingTop: "14px", fontSize: "13px", color: C.textDark },
      }, `Closed as "${check.status}"`
       + (check.resolved_by ? ` by ${check.resolved_by}` : "")
       + (check.resolution_note ? ` — ${check.resolution_note}` : "")),
    ),

    // ── History ─────────────────────────────────────────────────
    history.length > 0 && React.createElement("div", { style: cardStyle },
      React.createElement("h2", { style: h2Style }, "Previous checks"),
      React.createElement("p", {
        style: { fontSize: "12px", color: C.textMuted, margin: "6px 0 12px" },
      }, "Every check ever run, including clean ones. This list is the "
       + "record — nothing here can be deleted."),
      ...history.slice(0, 25).map((c) => React.createElement("button", {
        key: c.id,
        onClick: () => openCheck(c.id),
        className: "list-row",
        style: {
          width: "100%", display: "grid",
          gridTemplateColumns: "1.4fr 0.9fr 0.7fr 40px",
          alignItems: "center", textAlign: "left", gap: "8px",
          padding: "11px 0", background: "transparent", border: "none",
          borderTop: `1px solid ${C.warmGray}`, cursor: "pointer",
        },
      },
        React.createElement("div", {
          style: { fontSize: "13px", color: C.navy, fontWeight: 600 },
        }, (c.searched_names || []).join(", ")),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textMuted },
        }, c.intake_reference || (c.matter_id ? `Matter ${c.matter_id}` : "—")),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textMuted },
        }, `${c.hit_count} hit${c.hit_count === 1 ? "" : "s"} · ${c.status}`),
        React.createElement("div", {
          style: { fontSize: "11px", color: C.textLight, textAlign: "right" },
        }, c.run_at ? c.run_at.slice(0, 10) : ""),
      )),
    ),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const inputStyle = {
  width: "100%", padding: "9px 11px", borderRadius: "6px",
  border: "1px solid #E8E3DC", fontSize: "14px",
  fontFamily: "inherit", boxSizing: "border-box", background: "#fff",
};
const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "7px 13px", fontSize: "12px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "7px 15px", fontSize: "12px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
const tagStyle = {
  fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.06em", color: "#6B6660",
  border: "1px solid #E8E3DC", borderRadius: "4px", padding: "2px 7px",
};
